import { hasValue, styles } from "@repo/helpers";

import { InputComponent } from "../input";
import style from "./style.module.scss";
import { MarkupProps } from "./types";

export default function TextAreaMarkup(props: MarkupProps) {
	const { rows, cols, onChange, inputRef, minLength, maxLength, ...inputProps } = props;
	const { className, error, id, name, value, disabled, onKeyDown, readOnly } = inputProps;
	const showCounter = (hasValue(minLength) || hasValue(maxLength)) && !readOnly && !disabled;
	const counterId = showCounter && id ? `${id}Counter` : undefined;
	const adjProps = {
		className: styles(className, style.formControl, error && style.error, disabled && style.disabled),
		name: name || id,
		id: id,
		value: value ?? "",
		disabled: disabled,
		readOnly: readOnly,
		rows: rows,
		cols: cols,
		onChange: onChange,
		onKeyDown: onKeyDown,
		"aria-describedby": counterId,
	};
	const footer = showCounter ? (
		<CharacterCounter id={counterId} value={value} minLength={minLength} maxLength={maxLength} />
	) : null;
	return (
		<InputComponent {...inputProps} footer={footer}>
			<textarea {...adjProps} ref={inputRef} />
		</InputComponent>
	);
}

/**
 * Shows the count against the max, e.g. (42/250). Until the minimum is met the count is replaced
 * with how many characters are still needed as a negative, e.g. (-58/250).
 */
function CharacterCounter({ id, value, minLength, maxLength }: CounterProps) {
	const length = hasValue(value) ? String(value).length : 0;
	const short = hasValue(minLength) && length < minLength;
	const over = hasValue(maxLength) && length > maxLength;
	//Empty is left to the required check, so it isn't flagged as too short
	const invalid = over || (short && length > 0);
	const count = short ? -(minLength - length) : length;

	return (
		<div id={id} className={styles(style.counter, invalid && style.counterInvalid)}>
			{hasValue(maxLength) ? `(${count}/${maxLength})` : `(${count})`}
		</div>
	);
}

interface CounterProps {
	id?: string;
	value: MarkupProps["value"];
	minLength?: number;
	maxLength?: number;
}
